import React, { useEffect, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import { mediaUrl } from "../apiUrl";
import { AuthenticatedMedia } from "./AuthenticatedMedia";
import { BlogRow, createBlog, deleteBlog, fetchBlogs, updateBlog, setBlogPublication } from "./api";
import ActionToast, { ToastAction } from "./ActionToast";

const BlogImage: React.FC<{ src?: string | null; alt: string; className?: string; style?: React.CSSProperties }> = ({ src, alt, className, style }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return <span className="text-muted" role="img" aria-label="Image unavailable">Image unavailable</span>;
  return <img src={src} alt={alt} className={className} style={style} onError={() => setFailed(true)} />;
};

const BlogManagement: React.FC = () => {
  const [rows, setRows] = useState<BlogRow[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editRow, setEditRow] = useState<BlogRow | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [publicationBusy, setPublicationBusy] = useState<number | null>(null);
  const [editPreview, setEditPreview] = useState<string | null>(null);
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);

  const loadBlogs = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchBlogs();
      setRows(data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogs();
  }, []);

  useEffect(() => {
    if (!editRow?.imageFile) { setEditPreview(null); return; }
    const url = URL.createObjectURL(editRow.imageFile);
    setEditPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [editRow?.imageFile]);

  const validImage = (file: File) => file.size <= 5 * 1024 * 1024 && /\.(jpe?g|png|gif)$/i.test(file.name) && ['image/jpeg', 'image/png', 'image/gif'].includes(file.type);

  useEffect(() => {
    if (!toastAction) return;
    const timer = setTimeout(() => setToastAction(null), 6500);
    return () => clearTimeout(timer);
  }, [toastAction]);

  const toMediaUrl = (value?: string | null) => {
    if (!value) return "";
    return mediaUrl(value);
  };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || !image) {
      alert("Title, description, and image are required.");
      return;
    }
    if (!validImage(image)) { alert('Choose a JPEG, PNG or GIF image no larger than 5 MB.'); return; }
    setBusy(true);
    try {
      await createBlog(title.trim(), description.trim(), image);
      setTitle("");
      setDescription("");
      setImage(null);
      setShowAddModal(false);
      await loadBlogs();
      setToastAction("saved");
    } catch (err: any) {
      alert(err.message || "Failed to publish blog");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: number) => {
    if (!window.confirm("Delete this blog?")) return;
    try {
      await deleteBlog(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      setToastAction("deleted");
    } catch (err: any) {
      alert(err.message || "Delete failed");
    }
  };

  const onPublication = async (row: BlogRow) => {
    const status = row.publication_status === 'published' ? 'draft' : 'published';
    if (!window.confirm(`${status === 'published' ? 'Publish' : 'Unpublish'} this story?`)) return;
    setPublicationBusy(row.id);
    try {
      const result = await setBlogPublication(row.id, status);
      setRows(prev => prev.map(item => item.id === row.id ? { ...item, publication_status: result.publication_status } : item));
    } catch (err: any) { alert(err.message || 'Unable to update publication status.'); }
    finally { setPublicationBusy(null); }
  };

  const onEditSave = async () => {
    if (!editRow) return;
    if (!editRow.title?.trim() || !editRow.description?.trim()) {
      alert("Title and description are required.");
      return;
    }
    if (editRow.imageFile && !validImage(editRow.imageFile)) { alert('Choose a JPEG, PNG or GIF image no larger than 5 MB.'); return; }
    setEditBusy(true);
    try {
      const result = await updateBlog({
        ...editRow,
        title: editRow.title.trim(),
        description: editRow.description.trim(),
      });
      const updated = result.blog || editRow;
      setRows((prev) => prev.map((row) => (row.id === editRow.id ? { ...row, ...updated } : row)));
      setEditRow(null);
      setToastAction("saved");
    } catch (err: any) {
      alert(err.message || "Failed to update blog");
    } finally {
      setEditBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <section className="blog-mgmt">
        <ActionToast action={toastAction} />
        <h1 className="blog-mgmt-title">Blog Management</h1>
        {error && <div className="alert alert-danger" role="alert">{error} <button type="button" onClick={loadBlogs}>Retry</button></div>}
        <div className="d-flex justify-content-start mb-3">
          <button
            type="button"
            className="btn btn-success"
            onClick={() => setShowAddModal(true)}
          >
            <i className="fa-regular fa-pen-to-square me-2" />
            Add Blog
          </button>
        </div>

        {showAddModal && (
          <div className="dash-modal-overlay" onClick={() => setShowAddModal(false)}>
            <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
              <div className="dash-modal-header">
                <h3 className="dash-modal-title">Add Blog</h3>
                <button type="button" className="dash-modal-close" onClick={() => setShowAddModal(false)}>
                  x
                </button>
              </div>
              <div className="dash-modal-body">
                <form onSubmit={onCreate} className="blog-form">
                  <div>
                    <label className="blog-label">Title</label>
                    <input
                      className="blog-input"
                      placeholder="Enter blog title..."
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="blog-label">Description</label>
                    <textarea
                      className="blog-textarea"
                      placeholder="Write your blog content..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="blog-label">Cover Image</label>
                    <label className="blog-upload-box">
                      <i className="fa-regular fa-image blog-upload-icon" />
                      <span className="blog-upload-text">Click or drag to upload an image</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif"
                        className="blog-file-input"
                        onChange={(e) => setImage(e.target.files?.[0] || null)}
                      />
                    </label>
                    {image && <div className="blog-selected-file">{image.name}</div>}
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowAddModal(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-success" disabled={busy}>
                      {busy ? "Saving..." : "Save Blog"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        <div className="blog-card">
          <div className="d-flex justify-content-between align-items-center px-3 pt-3">
            <h2 className="h5 mb-0">All Blogs</h2>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0 blog-list-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {row.imageUrl ? <AuthenticatedMedia endpoint={`/files/admin/blogs/${row.id}`} alt={row.title} className="blog-thumb" /> : null}
                    </td>
                    <td className="blog-title-cell" title={row.title || ""}>
                      {row.title}
                    </td>
                    <td className="blog-desc-cell" title={row.description || ""}>
                      {row.description || "-"}
                    </td>
                    <td>{row.publication_status === 'published' ? 'Published' : row.publication_status === 'draft' ? 'Draft' : 'Unavailable'}</td>
                    <td className="text-end">
                      <div className="action-icons justify-content-end">
                        <button type="button" className="btn btn-outline-secondary" disabled={publicationBusy !== null} onClick={() => onPublication(row)}>{row.publication_status === 'published' ? 'Unpublish' : 'Publish'}</button>
                        <button
                          type="button"
                          className="icon-btn"
                          title="Edit"
                          onClick={() => setEditRow({ ...row })}
                        >
                          <i className="fa-regular fa-pen-to-square" />
                        </button>
                        <button
                          type="button"
                          className="icon-btn icon-btn-danger"
                          title="Delete"
                          onClick={() => onDelete(row.id)}
                        >
                          <i className="fa-regular fa-trash-can" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {loading && rows.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-4">Loading blogs...</td></tr>
                )}
                {!loading && !error && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No blogs yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {editRow && (
          <div className="dash-modal-overlay" onClick={() => setEditRow(null)}>
            <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
              <div className="dash-modal-header">
                <h3 className="dash-modal-title">Edit Blog</h3>
                <button type="button" className="dash-modal-close" onClick={() => setEditRow(null)}>
                  x
                </button>
              </div>
              <div className="dash-modal-body">
                <div className="edit-grid">
                  <div className="edit-field full">
                    <label className="edit-label">Title</label>
                    <input
                      className="form-control"
                      placeholder="Enter blog title"
                      value={editRow.title || ""}
                      onChange={(e) => setEditRow({ ...editRow, title: e.target.value })}
                    />
                  </div>
                  <div className="edit-field full">
                    <label className="edit-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="Write blog description"
                      value={editRow.description || ""}
                      onChange={(e) => setEditRow({ ...editRow, description: e.target.value })}
                    />
                  </div>
                  <div className="edit-field full">
                    <label className="edit-label">Image</label>
                    <BlogImage
                        src={editPreview || toMediaUrl(editRow.imageUrl)}
                        alt={editRow.title}
                        style={{ width: "140px", height: "90px", objectFit: "cover", borderRadius: "10px", border: "1px solid #dfe5ef", marginBottom: "10px" }}
                      />
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif"
                      className="form-control"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setEditRow({ ...editRow, imageFile: file });
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="dash-modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setEditRow(null)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={editBusy} onClick={onEditSave}>
                  {editBusy ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </DashboardLayout>
  );
};

export default BlogManagement;
